import { isDefined } from '@eih-netbroker/common';
import { InputHTMLAttributes, KeyboardEvent, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { NumberFormatValues, NumericFormatProps } from 'react-number-format';
import { useTheme } from '../../../../../context/NbThemeContext';
import { NbLoader } from '../../../../../shared/components/loader/Loader';
import { NbNumericFormat } from '../../../../../shared/components/numeric-format/numericFormat';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import classes from './OrderFormNumberInput.module.scss';

const BASE_INPUT_FONT_SIZE = 48;
const BASE_SUFFIX_FONT_SIZE = 30;
const MIN_SCALE = 0.45;
const VALUE_ROW_GAP = 4;

export type OrderFormNumberInputSubRow = {
  id: string;
  content: ReactNode;
};

export type OrderFormNumberInputProps = {
  label: ReactNode;
  value?: number;
  onChange: (value: number | undefined) => void;
  suffix?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  testId?: string;
  decimalScale?: number;
  maxValue?: number;
  subRows?: OrderFormNumberInputSubRow[];
  actionSlot?: ReactNode;
  maxFontSize?: number;
  maxSuffixFontSize?: number;
  isLoading?: boolean;
  errorText?: ReactNode;
  warningText?: ReactNode;
};

type AutoSizeInputProps = InputHTMLAttributes<HTMLInputElement> & {
  ref?: React.Ref<HTMLInputElement>;
  label: string;
  fontScale?: number;
  onBaseWidthChange?: (width: number) => void;
  maxFontSize: number;
};

/** Bare input that auto-sizes to its formatted content via a hidden measuring span. */
const AutoSizeInput = ({
  ref,
  label,
  value,
  placeholder,
  fontScale = 1,
  maxFontSize,
  onBaseWidthChange,
  style,
  ...rest
}: AutoSizeInputProps) => {
  const { rem } = useTheme();
  const [baseWidth, setBaseWidth] = useState<number>(0);
  const measureRef = useRef<HTMLSpanElement>(null);

  const displayValue = value !== undefined && value !== '' ? String(value) : placeholder ?? '';

  useLayoutEffect(() => {
    if (measureRef.current) {
      const measuredWidth = measureRef.current.scrollWidth;
      setBaseWidth(measuredWidth);
      onBaseWidthChange?.(measuredWidth);
    }
  }, [displayValue, maxFontSize, onBaseWidthChange, rem]);

  const scaledWidth = Math.max(baseWidth * fontScale, rem(24));

  return (
    <span className={classes.autoSizeWrapper}>
      <span
        ref={measureRef}
        className={cssWhen(classes.autoSizeMeasure, classes.input)}
        style={{
          fontSize: rem(maxFontSize),
        }}
        aria-hidden
      >
        {displayValue || '\u00A0'}
      </span>
      <input
        name={`order-form-number-input-${label}`}
        ref={ref}
        className={classes.input}
        value={value}
        placeholder={placeholder}
        style={{
          ...style,
          width: scaledWidth,
          fontSize: rem(maxFontSize * fontScale),
        }}
        {...rest}
      />
    </span>
  );
};

export const OrderFormNumberInput = ({
  label,
  value,
  onChange,
  suffix,
  disabled,
  readOnly,
  className,
  testId,
  decimalScale = 0,
  maxValue = 999_999_999.9999,
  subRows = [],
  actionSlot,
  maxFontSize = BASE_INPUT_FONT_SIZE,
  maxSuffixFontSize = BASE_SUFFIX_FONT_SIZE,
  isLoading,
  errorText,
  warningText,
}: OrderFormNumberInputProps) => {
  const { rem } = useTheme();
  const [inputBaseWidth, setInputBaseWidth] = useState(0);
  const [suffixBaseWidth, setSuffixBaseWidth] = useState(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const valueRowRef = useRef<HTMLDivElement>(null);
  const suffixMeasureRef = useRef<HTMLSpanElement>(null);

  const handleBaseWidthChange = useCallback((width: number): void => {
    setInputBaseWidth(width);
  }, []);

  useLayoutEffect(() => {
    if (isLoading) return;

    const valueRow = valueRowRef.current;
    if (!valueRow) return;

    setAvailableWidth(valueRow.clientWidth);

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setAvailableWidth(entry.contentRect.width);
      }
    });
    observer.observe(valueRow);
    return () => observer.disconnect();
  }, [isLoading]);

  useLayoutEffect(() => {
    setSuffixBaseWidth(suffixMeasureRef.current?.scrollWidth ?? 0);
  }, [isLoading, maxSuffixFontSize, rem, suffix]);

  const gapWidth = suffix ? rem(VALUE_ROW_GAP) : 0;
  const scalableContentBaseWidth = inputBaseWidth + suffixBaseWidth;
  const availableScalableWidth = Math.max(availableWidth - gapWidth, 0);
  const fontScale =
    availableWidth > 0 && scalableContentBaseWidth > availableScalableWidth
      ? Math.max(MIN_SCALE, availableScalableWidth / scalableContentBaseWidth)
      : 1;

  const handleValueChange = ({ floatValue }: NumberFormatValues): void => {
    onChange(floatValue);
  };

  const isAllowed: NumericFormatProps['isAllowed'] = ({ floatValue, value: stringValue }) => {
    // Prevent clearing a lone zero, which would drop the field into placeholder state.
    if (value === 0 && floatValue === undefined) {
      return false;
    }
    // Prevent leading zeros such as "00" or "007" (allows a single "0" and decimals like "0.5").
    if (/^0\d/.test(stringValue)) {
      return false;
    }
    if (floatValue === undefined) {
      return true;
    }
    return floatValue <= maxValue;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const input = event.currentTarget;
    // Only guard the lone integer zero state (a single "0" with no decimal part yet).
    if (value !== 0 || input.value.length > 1) {
      return;
    }
    // Block deleting the lone zero so the caret stays in place and the field never empties.
    if (event.key === 'Backspace' || event.key === 'Delete' || event.key === '0') {
      event.preventDefault();
      return;
    }
    // Replace the lone zero with a typed non-zero digit instead of producing a leading zero.
    if (/^[1-9]$/.test(event.key)) {
      input.select();
    }
  };

  return (
    <div className={cssWhen(classes.container, className)}>
      <span
        className={classes.label}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {label}
      </span>
      <div className={cssWhen(classes.surface, [isDefined(errorText), classes.surfaceIsError])}>
        {isLoading && (
          <NbLoader
            horizontalCenter
            verticalCenter
          />
        )}
        {!isLoading && (
          <>
            <div
              ref={valueRowRef}
              className={classes.valueRow}
            >
              {suffix && (
                <span
                  ref={suffixMeasureRef}
                  className={classes.suffixMeasure}
                  style={{ fontSize: rem(maxSuffixFontSize) }}
                  aria-hidden
                >
                  {suffix}
                </span>
              )}
              <NbNumericFormat<AutoSizeInputProps>
                value={value ?? ''}
                label={String(label)}
                decimalScale={decimalScale}
                onValueChange={handleValueChange}
                isAllowed={isAllowed}
                onKeyDown={handleKeyDown}
                customInput={AutoSizeInput}
                fontScale={fontScale}
                onBaseWidthChange={handleBaseWidthChange}
                disabled={disabled}
                readOnly={readOnly}
                data-testid={testId}
                maxFontSize={maxFontSize}
                autoComplete="off"
              />
              {suffix && (
                <span
                  className={classes.suffix}
                  style={{ fontSize: rem(maxSuffixFontSize * fontScale) }}
                >
                  {suffix}
                </span>
              )}
            </div>
            {subRows.length > 0 && (
              <div className={classes.subRows}>
                {subRows.map(subRow => (
                  <div
                    key={subRow.id}
                    className={classes.subRow}
                  >
                    {subRow.content}
                  </div>
                ))}
              </div>
            )}
            {isDefined(errorText) && (
              <span
                className={classes.errorText}
                data-testid={testId ? `${testId}-error` : undefined}
              >
                {errorText}
              </span>
            )}
            {!isDefined(errorText) && isDefined(warningText) && (
              <span
                className={classes.warningText}
                data-testid={testId ? `${testId}-warning` : undefined}
              >
                {warningText}
              </span>
            )}
            {actionSlot && <div className={classes.action}>{actionSlot}</div>}
          </>
        )}
      </div>
    </div>
  );
};
